import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Cpu } from "lucide-react";

const FEATURE_LABEL = {
    ask_vault:          "Ask VAULT · warm phrasing",
    inbox_classify:     "VAULT Inbox · document classification",
    concierge_phrasing: "Concierge · attention-card phrasing",
};

const MODEL_TONE = {
    "claude-sonnet-5":            "bg-amber-50 text-amber-800 border-amber-200",
    "claude-haiku-4-5-20251001":  "bg-emerald-50 text-emerald-800 border-emerald-200",
    "claude-opus-4-8":            "bg-blue-50 text-blue-800 border-blue-200",
};

export default function ClaudeSettings() {
    const [state, setState] = useState({ catalog: [], features: [], selection: {} });
    const [busy, setBusy] = useState(false);

    const load = () => {
        setBusy(true);
        api.get("/ai/claude/models").then(r => setState(r.data)).finally(() => setBusy(false));
    };
    useEffect(() => { load(); }, []);

    const set = async (feature, model) => {
        try {
            await api.post("/ai/claude/models", { feature, model });
            toast.success(`${FEATURE_LABEL[feature]} → ${model}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update selection");
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="claude-settings-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Cpu size={12} /> Admin · Claude AI Models
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                        Pick the Claude model for each surface.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Claude never invents numbers. The deterministic VAULT engine still produces every fact;
                        Claude only restates and classifies. Selections take effect on the next request.
                    </p>
                </div>
                <button onClick={load} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold border border-stone-300 rounded-full px-3.5 py-2 hover:border-stone-500"
                        data-testid="claude-refresh">
                    <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="space-y-3">
                {state.features.map(f => (
                    <div key={f} className="rounded-2xl border border-stone-200 bg-white p-4"
                         data-testid={`claude-feature-${f}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles size={13} className="text-amber-700" />
                            <div className="text-[13.5px] font-semibold text-stone-900">{FEATURE_LABEL[f]}</div>
                            <span className={`ml-auto text-[10.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${MODEL_TONE[state.selection[f]] || "bg-stone-100 text-stone-600 border-stone-200"}`}>
                                {state.selection[f] || "default"}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {state.catalog.map(m => (
                                <button key={m} onClick={() => set(f, m)}
                                        className={`text-[11.5px] font-semibold px-3 py-1 rounded-full border transition-colors ${state.selection[f] === m ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700 hover:border-stone-500"}`}
                                        data-testid={`claude-${f}-${m}`}>
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 border-l-2 border-amber-300 bg-amber-50/40 px-4 py-3 text-[12px] text-stone-700">
                <strong>Emergent Universal Key</strong> — every Claude call flows through <code>EMERGENT_LLM_KEY</code>
                via <code>emergentintegrations</code>. Budget can be topped up from Profile → Manage plan → Universal Key.
            </div>
        </div>
    );
}
